import {resolveAllFormulas} from '../../../../../../../core/services/formula-engine';

/**
 * Decimal places for formula-resolved live prices on the product prices page.
 * Should match how prices are stored on the server (typically 2).
 */
export const FORMULA_RESOLVED_PRICE_DECIMAL_PLACES = 2;

/**
 * Rounds a numeric price to the configured decimal places (standard Math.round half-away-from-zero).
 *
 * @param {number | null | undefined} value
 * @param {number} [decimalPlaces]
 * @returns {number | null}
 */
export function roundFormulaResolvedPrice(value, decimalPlaces = FORMULA_RESOLVED_PRICE_DECIMAL_PLACES) {
    if (value == null || typeof value !== 'number' || Number.isNaN(value)) {
        return null;
    }
    const factor = 10 ** decimalPlaces;
    return Math.round(value * factor) / factor;
}

/**
 * Tolerance for treating a live price as matching a formula-resolved price after rounding.
 * Half of the smallest currency unit at the configured precision (e.g. $0.005 at 2 decimals).
 *
 * @param {number} [decimalPlaces]
 * @returns {number}
 */
export function formulaResolvedPriceDiscrepancyTolerance(decimalPlaces = FORMULA_RESOLVED_PRICE_DECIMAL_PLACES) {
    return 0.5 * 10 ** -decimalPlaces;
}

/**
 * Re-keys 1-based engine output to priceLevelId and applies {@link roundFormulaResolvedPrice}.
 *
 * @param {Record<number, number|null>} resolvedByIndex
 * @param {Array<{id: number}>} livePriceLevels
 * @param {number} [decimalPlaces]
 * @returns {Record<number, number|null>}
 */
export function mapRoundedResolvedValuesByPriceLevelId(
    resolvedByIndex,
    livePriceLevels,
    decimalPlaces = FORMULA_RESOLVED_PRICE_DECIMAL_PLACES,
) {
    const resolvedValues = {};
    livePriceLevels.forEach((pl, i) => {
        resolvedValues[pl.id] = roundFormulaResolvedPrice(resolvedByIndex[i + 1] ?? null, decimalPlaces);
    });
    return resolvedValues;
}

/**
 * Runs the formula engine and returns price-level resolved values rounded for this page.
 *
 * @param {Record<number, string>} formulaMap
 * @param {Record<number, number|null>} baseByIndex
 * @param {Record<string, number|null>} specialResolvedValues
 * @param {Array<{id: number}>} livePriceLevels
 * @param {number} [decimalPlaces]
 * @returns {{ resolvedValues: Record<number, number|null>, resolvedByIndex: Record<number, number|null>, errors: Record<number, string> }}
 */
export function resolveAllFormulasWithRoundedPrices(
    formulaMap,
    baseByIndex,
    specialResolvedValues,
    livePriceLevels,
    decimalPlaces = FORMULA_RESOLVED_PRICE_DECIMAL_PLACES,
) {
    const {resolvedValues: resolvedByIndex, errors} = resolveAllFormulas(formulaMap, baseByIndex, specialResolvedValues);
    return {
        resolvedByIndex,
        errors,
        resolvedValues: mapRoundedResolvedValuesByPriceLevelId(resolvedByIndex, livePriceLevels, decimalPlaces),
    };
}
